/**
 * `/analytics` — pipeline health, the enquiry→won funnel, line-weighted purchaser workload, and
 * conversion by size / customer / type. Prototype `vAnalytics`; design at
 * `docs/superpowers/specs/2026-08-09-analytics-dashboard-design.md`.
 *
 * ---- Where the numbers come from ----
 *
 * The KPI tiles and blockers come from `analytics.get_pipeline_analytics`, a real server
 * aggregate. Everything else is computed here from live records — `Enquiry`, `Enquiry Item` and
 * `Version` — because the three methods that would serve those boards (asks E14-E16) do not exist
 * on the live server. See `src/lib/analytics/aggregate.ts` for why that trade was made and what
 * it costs.
 *
 * One fetch feeds every derived board (`useAnalyticsSources`), so the page has one loading state
 * rather than four, and the boards cannot disagree with each other about what data they saw.
 */
import { useMemo, useState } from 'react'
import { buildSourcing, filterFacts, type FactFilters } from '../../lib/analytics/sourcing'
import { describeExcluded, withoutExcludedPurchasers } from '../../lib/analytics/excludedAccounts'
import { SourcingBoard } from './SourcingBoard'
import type { EnquiryFact } from '../../lib/analytics/types'
import { useSession } from '../../components/SessionProvider'
import { useAnalyticsSources, useQuotationValues, useSourcingSources } from '../../lib/api/useAnalyticsSources'
import { filterValuesByRange, summarizeValues, toValueFacts } from '../../lib/analytics/value'
import { getBaseCurrency } from '../../lib/api/baseCurrency'
import { ValueBoard } from './ValueBoard'
import {
  buildConversionBreakdown,
  buildFunnel,
  buildKpis,
  buildWorkload,
} from '../../lib/analytics/aggregate'
import {
  filterFactsByRange,
  presetAvailability,
  presetRange,
  previousRange,
  transitionsForFacts,
  type RangePresetId,
} from '../../lib/analytics/range'
import { usePipelineAnalytics } from '../../lib/api/useAnalytics'
import { KpiRow } from './KpiRow'
import { BlockersBoard } from './BlockersBoard'
import { FunnelBoard } from './FunnelBoard'
import { StageTimeBoard } from './StageTimeBoard'
import { WorkloadBoard } from './WorkloadBoard'
import { ConversionBoard } from './ConversionBoard'
import { PageHelpButton } from '../../components/PageHelp'

export function AnalyticsPage() {
  const { role } = useSession()
  // A role the session did not recognise is null, and is treated as NON-manager — never coerced
  // upward. Matches the prototype's `STATE.role === 'Purchase Manager'` gate.
  const isManager = role === 'purchase_manager'

  const [preset, setPreset] = useState<RangePresetId>('all')
  // The manager's filters (Akhil, 2026-09-19) — applied after the range, to every board below.
  const [filters, setFilters] = useState<FactFilters>({})
  // The clock is read once, here, and passed down as a plain date string so `range.ts` stays pure.
  const today = new Date().toISOString().slice(0, 10)
  const excludedNote = describeExcluded()

  const { data: allData, isLoading, error } = useAnalyticsSources()
  /*
    Bulk-loaded accounts are left out of every board on this screen (2026-09-22,
    `analytics/excludedAccounts.ts`) — at the source, so the purchaser table, the KPIs above it
    and the filter list cannot disagree about who is counted.
  */
  const data = useMemo(
    () => (allData ? { ...allData, facts: withoutExcludedPurchasers(allData.facts) } : allData),
    [allData],
  )

  // Which presets can actually narrow anything, given the data present. Until it loads, offer them
  // all rather than flashing a row of disabled buttons.
  const presets = useMemo(
    () => presetAvailability(data?.facts ?? [], today),
    [data, today],
  )
  const chosen = presets.find((p) => p.id === preset)
  // A preset can become disabled when the data loads or refreshes under it. Falling back to All
  // time keeps the page on a selection that means something, rather than leaving it on a window
  // the UI now refuses to let anyone pick.
  const activePreset: RangePresetId = !data || !chosen?.disabled ? preset : 'all'
  const range = useMemo(() => presetRange(activePreset, today), [activePreset, today])
  // Quotation values, filtered by the same range as every other board and summed in the browser.
  const values = useQuotationValues()
  const baseCurrency = getBaseCurrency()
  const valueFacts = useMemo(() => (values.data ? filterValuesByRange(toValueFacts(values.data, baseCurrency), range) : undefined), [values.data, range, baseCurrency])
  const valueSummary = useMemo(() => (valueFacts ? summarizeValues(valueFacts, baseCurrency) : undefined), [valueFacts, baseCurrency])

  // Blockers are the server's judgement; only their scope is applied locally (see BlockersBoard).
  const pipeline = usePipelineAnalytics({})

  const derived = useMemo(() => {
    if (!data) return undefined
    const facts = filterFacts(filterFactsByRange(data.facts, range), filters)
    // The preceding equal-length window, for the KPI deltas. Null for an unbounded range — there
    // is no "previous" to All time, and inventing one would fabricate a trend.
    const prev = previousRange(range)
    return {
      facts,
      kpis: buildKpis(facts),
      previousKpis: prev ? buildKpis(filterFacts(filterFactsByRange(data.facts, prev), filters)) : null,
      funnel: buildFunnel(facts, transitionsForFacts(data.transitions, facts)),
      workload: buildWorkload(facts),
      conversion: buildConversionBreakdown(facts),
    }
  }, [data, range, filters])

  // The filter options come from the whole dataset, so a choice never disappears once made.
  const filterOptions = useMemo(() => {
    const opt = (pick: (f: EnquiryFact) => [string, string]) => {
      const m = new Map<string, string>()
      for (const f of data?.facts ?? []) {
        const [value, label] = pick(f)
        if (value) m.set(value, label || value)
      }
      return [...m].sort((a, b) => a[1].localeCompare(b[1]))
    }
    return {
      purchasers: opt((f) => [f.purchaser, f.purchaser_name]),
      customers: opt((f) => [f.customer, f.customer_name]),
      types: opt((f) => [f.enquiry_type, f.enquiry_type]),
      statuses: opt((f) => [f.status, f.status]),
    }
  }, [data])

  // Sourcing behaviour, manager-only: four more lists, read only when the board is shown.
  const sourcing = useSourcingSources()
  const sourcingRows = useMemo(
    () => (derived && sourcing.data ? buildSourcing(derived.facts, sourcing.data) : undefined),
    [derived, sourcing.data],
  )

  const inRange = new Set(derived?.facts.map((f) => f.id) ?? [])
  const blockedCount = (pipeline.data?.blockers ?? []).filter((b) => inRange.has(b.id)).length

  return (
    <>
      <div
        className="head"
        style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}
      >
        <div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            Analytics
            <PageHelpButton page="analytics" />
          </h1>
          <p>
            {isManager
              ? 'Pipeline health, conversion, and where the work actually sits — team-wide.'
              : 'Pipeline health and conversion. Purchaser comparison is manager-only.'}
          </p>
        </div>
        <div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {presets.map((p) => (
              <button
                key={p.id}
                type="button"
                className={p.id === activePreset ? 'btn sm pri' : 'btn sm'}
                disabled={p.disabled}
                title={p.disabled ? p.reason : `${p.count} enquiries`}
                style={p.disabled ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
                onClick={() => setPreset(p.id)}
              >
                {p.label}
              </button>
            ))}
          </div>
          {/*
            The dates and the count are shown because on a young dataset every preset can select
            the same rows — every enquiry on the bench today was raised inside one week. Without
            this line the buttons look inert when they are in fact working; with it, the selected
            window is visible even when the numbers below it do not move.
          */}
          {/* The filters (2026-09-19). Each narrows every board; blank means any. */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end', marginTop: 8 }}>
            <FilterSelect label="Purchaser" value={filters.purchaser ?? ''} options={filterOptions.purchasers} onChange={(v) => setFilters((f) => ({ ...f, purchaser: v || undefined }))} />
            <FilterSelect label="Customer" value={filters.customer ?? ''} options={filterOptions.customers} onChange={(v) => setFilters((f) => ({ ...f, customer: v || undefined }))} />
            <FilterSelect label="Type" value={filters.enquiry_type ?? ''} options={filterOptions.types} onChange={(v) => setFilters((f) => ({ ...f, enquiry_type: v || undefined }))} />
            <FilterSelect label="Status" value={filters.status ?? ''} options={filterOptions.statuses} onChange={(v) => setFilters((f) => ({ ...f, status: v || undefined }))} />
            {Object.values(filters).some(Boolean) ? (
              <button type="button" className="btn sm" onClick={() => setFilters({})}>
                Clear filters
              </button>
            ) : null}
          </div>
          <div style={{ color: 'var(--muted)', fontSize: 11.5, marginTop: 6, textAlign: 'right' }}>
            {range.from && range.to ? `${range.from} → ${range.to}` : 'No date limit'}
            {derived ? ` · ${derived.facts.length} of ${data?.facts.length ?? 0} enquiries` : ''}
            {/* Said out loud: a number that quietly leaves rows out is a number nobody can check. */}
            {excludedNote && allData ? (
              <div>
                excluding {excludedNote} ({allData.facts.length - (data?.facts.length ?? 0)} enquiries)
              </div>
            ) : null}
          </div>
          {data && presets.some((p) => p.disabled) && (
            <div style={{ color: 'var(--muted)', fontSize: 11, marginTop: 3, textAlign: 'right', maxWidth: 340 }}>
              Greyed-out ranges would select the same enquiries as <b>All time</b> — every enquiry
              so far was raised inside one week. They enable themselves once there is more history.
            </div>
          )}
        </div>
      </div>

      <KpiRow
        kpis={derived?.kpis}
        previous={derived?.previousKpis ?? null}
        isLoading={isLoading}
        error={error}
        blockedCount={blockedCount}
      />

      <div className="row" style={{ marginTop: 14, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 320 }}>
          <FunnelBoard funnel={derived?.funnel} isLoading={isLoading} error={error} />
        </div>
        <div style={{ flex: 1, minWidth: 320 }}>
          <StageTimeBoard funnel={derived?.funnel} isLoading={isLoading} error={error} />
        </div>
      </div>

      {isManager && (
        <div style={{ marginTop: 14 }}>
          <WorkloadBoard workload={derived?.workload} isLoading={isLoading} error={error} />
        </div>
      )}

      {isManager && (
        <div style={{ marginTop: 14 }}>
          <SourcingBoard rows={sourcingRows} isLoading={isLoading || sourcing.isLoading} error={error ?? sourcing.error} />
        </div>
      )}

      <div style={{ marginTop: 14 }}>
        <ConversionBoard
          breakdown={derived?.conversion}
          isLoading={isLoading}
          error={error}
          isManager={isManager}
        />
      </div>

      {/* Money — the one thing the server's pipeline analytics never carried (Akhil, 2026-09-17). */}
      <div style={{ marginTop: 14 }}>
        <ValueBoard facts={valueFacts} summary={valueSummary} isLoading={values.isPending} error={values.error} />
      </div>
      <div style={{ marginTop: 14 }}>
        <BlockersBoard facts={derived?.facts} />
      </div>

      <p style={{ color: 'var(--muted)', fontSize: 11, marginTop: 16 }}>
        Funnel, workload and conversion are computed in the browser from live enquiry records and
        status history, because the server has no aggregate for them yet (asks E14–E16). The KPI
        tiles and blockers come from <code>analytics.get_pipeline_analytics</code>.
      </p>
    </>
  )
}

function FilterSelect({ label, value, options, onChange }: { label: string; value: string; options: [string, string][]; onChange: (v: string) => void }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} title={label} style={{ fontSize: 12, padding: '3px 6px' }}>
      <option value="">{label}: any</option>
      {options.map(([v, l]) => (
        <option key={v} value={v}>
          {l}
        </option>
      ))}
    </select>
  )
}
